import { createFileRoute } from "@tanstack/react-router";
import { site } from "@/lib/site";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy - WeLivdIt" },
      { name: "description", content: "How WeLivdIt collects, uses, and protects your personal information." },
      { property: "og:title", content: "Privacy Policy - WeLivdIt" },
      { property: "og:description", content: "Read how WeLivdIt collects, uses, stores, and safeguards personal data from visa coaching students." },
      { property: "og:url", content: "https://welivdit.com/privacy" },
      { property: "og:image", content: "https://welivdit.com/og-share-v6.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: "https://welivdit.com/og-share-v6.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://welivdit.com/privacy" }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <section className="mx-auto max-w-3xl px-5 py-16 md:py-24">
      <p className="text-sm font-medium text-primary uppercase tracking-wider">Legal</p>
      <h1 className="mt-2 font-display text-4xl md:text-5xl font-semibold tracking-tight">Privacy Policy</h1>
      <p className="mt-3 text-sm text-muted-foreground">Last updated: {new Date().toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" })}</p>

      <div className="mt-10 space-y-8 text-foreground/80 leading-relaxed">
        <p>WeLivdIt ("we", "us") respects your privacy. This policy explains what information we collect, why we collect it, and how we protect it.</p>

        <section>
          <h2 className="font-display text-2xl font-semibold text-foreground">1. Information we collect</h2>
          <ul className="mt-2 list-disc pl-5 space-y-1">
            <li><strong>Contact details:</strong> name, email, WhatsApp number.</li>
            <li><strong>Visa prep details:</strong> intended university, program, intake, interview date, DS-160 information you choose to share.</li>
            <li><strong>Payment data:</strong> handled directly by Razorpay. We never see or store your full card / UPI / bank credentials.</li>
            <li><strong>Usage data:</strong> basic analytics like pages visited and approximate location, used to improve the site.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl font-semibold text-foreground">2. How we use your information</h2>
          <ul className="mt-2 list-disc pl-5 space-y-1">
            <li>To deliver coaching sessions, mock interviews, and DS-160 reviews.</li>
            <li>To send service-related updates over email or WhatsApp.</li>
            <li>To process payments and issue receipts via Razorpay.</li>
            <li>To improve our content, question banks, and platform.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl font-semibold text-foreground">3. Sharing of information</h2>
          <p className="mt-2">We do not sell your data. We share it only with:</p>
          <ul className="mt-2 list-disc pl-5 space-y-1">
            <li>Razorpay, for payment processing.</li>
            <li>Our hosting and email/WhatsApp delivery providers, strictly to operate the service.</li>
            <li>Authorities, if required by law.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl font-semibold text-foreground">4. Data retention</h2>
          <p className="mt-2">We keep your data for as long as needed to provide the service and to comply with legal, accounting, and reporting obligations. You can request deletion at any time (see "Your rights" below).</p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-semibold text-foreground">5. Security</h2>
          <p className="mt-2">We use industry-standard practices - HTTPS, access controls, and row-level security on our database - to protect your information. No system is 100% secure, but we work hard to keep yours safe.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-semibold text-foreground">6. Your rights</h2>
          <p className="mt-2">You can ask us to access, correct, or delete your personal data at any time. Email <a href={`mailto:${site.email}`} className="text-primary underline">{site.email}</a> and we'll respond within a reasonable time frame.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-semibold text-foreground">7. Cookies</h2>
          <p className="mt-2">We use minimal cookies for essential site functionality and basic analytics. No third-party advertising cookies.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-semibold text-foreground">8. Children</h2>
          <p className="mt-2">Our services are intended for users 18 and older. If you are a minor, a parent or guardian must purchase and supervise on your behalf.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-semibold text-foreground">9. Contact</h2>
          <p className="mt-2">For privacy questions, email <a href={`mailto:${site.email}`} className="text-primary underline">{site.email}</a>.</p>
        </section>
      </div>
    </section>
  );
}
